import './App.css'
import { useState, useRef, useEffect, useCallback } from 'react'

const rows = 6
const cols = 5

const keyboardLayout = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'Backspace'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä', 'JAA'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'Enter']
]

const allowedChars = keyboardLayout
  .flat()
  .filter((char) => char !== 'JAA')



function App() {

  const [ guess, setGuess ] = useState([])
  const [ game, setGame ] = useState()
  const [flippingRow, setFlippingRow] = useState(null)
  const [notification, setNotification] = useState({ type: '', message: '', visible: false })
  const mounted = useRef(null)
  const notificationTimeout = useRef(null)
  const previousGuessCount = useRef(0)

  const showNotification = useCallback((type, message, timeout = 4000) => {
    if (notificationTimeout.current) {
      clearTimeout(notificationTimeout.current)
    }

    mounted.current && setNotification({ type, message, visible: true })

    notificationTimeout.current = setTimeout(() => {
      mounted.current && setNotification({ type: '', message: '', visible: false })
    }, timeout)
  }, [])

  const fetchGameData = useCallback(async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/game', {
        credentials: 'include'
      })

      if (!response.ok) {
        showNotification('error', 'Pelin lataaminen epäonnistui')
        return
      }

      const game = await response.json()
      mounted.current && setGame(game)
    } catch (err) {
      showNotification('error', 'Yhteysvirhe pelipalveluun')
    }
  }, [ showNotification ])

  const sendWord = useCallback(async (e) => {
    e?.preventDefault?.()

    if (guess.length < cols) {
      showNotification('error', 'Liian lyhyt sana')
      return
    }

    try {
      const guessResponse = await fetch(process.env.REACT_APP_API_URL + '/guess', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(guess),
        credentials: 'include'
      })

      if (!guessResponse.ok) {
        showNotification('error', 'Arvauksen lähetys epäonnistui')
        return
      }

      fetchGameData()
      const notificationJson = await guessResponse.json()

      if (notificationJson.type !== 'error') {
        setGuess([])
      }

      showNotification(notificationJson.type, notificationJson.message)
    } catch (err) {
      showNotification('error', 'Yhteysvirhe pelipalveluun')
    }
  }, [ guess, fetchGameData, showNotification ])

  const handleCharInput = useCallback((e) => {

    if (game?.isComplete) return

    const rawChar = e.key ?? e.target.value
    const char = rawChar?.length === 1 ? rawChar.toLowerCase() : rawChar

    if (char === 'Enter') {
      sendWord(e)
      return
    }

    if (!allowedChars.includes(char)) return

    if (char === 'Backspace' && guess.length > 0) {
      setGuess(prev => prev.slice(0, -1))
    } else if (char !== 'Backspace' && guess.length < cols){
      setGuess(prev => prev.concat(char))
    }
  }, [ sendWord, guess, game?.isComplete ])


  useEffect(() => {
    fetchGameData()
  }, [fetchGameData])

  useEffect(() => {
    mounted.current = true
    document.addEventListener('keydown', handleCharInput)

    return () => {
      mounted.current = false
      if (notificationTimeout.current) {
        clearTimeout(notificationTimeout.current)
      }
      document.removeEventListener('keydown', handleCharInput)
    }
  }, [ handleCharInput ] )

  useEffect(() => {
    const currentGuessCount = game?.guesses?.length ?? 0

    if (currentGuessCount > previousGuessCount.current) {
      setFlippingRow(currentGuessCount - 1)
    }

    previousGuessCount.current = currentGuessCount
  }, [game?.guesses?.length])

  const getCell = (row, col) => {
    if (!game) return null

    // check active quess
    if (!game.guesses[row] && game.guesses.length === row && guess.length <= cols && typeof guess[col] == 'string') {
      return <span className="letter" data-state="guess">{ guess[col] }</span>
    }

    // check previous guesses
    if (!game || !game.guesses[row] || !game.guesses[row][col] ) return null

    const [ char, status ] = game.guesses[row][col]
    const shouldFlip = row === flippingRow

    return (
      <span
        className={`letter${shouldFlip ? ' letter--flip' : ''}`}
        style={shouldFlip ? { '--flip-delay': `${col * 120}ms` } : undefined}
        data-state={status}
      >
        { char }
      </span>
    )
  }

  const copyToClipboard = useCallback(async (str) => {
    try {
      await navigator.clipboard.writeText(str)
      showNotification('success', 'Tulos kopioitu leikepöydälle')
    } catch (err) {
      showNotification('error', 'Tuloksen kopioiminen epäonnistui')
    }
  }, [ showNotification ])

  const handleShare = useCallback(async () => {
    if (!game || !game.guesses) {
      showNotification('error', 'Peliä ei ole vielä ladattu')
      return
    }

    const getEmoji = (status) => {
      switch (status) {
        case 'correct': return '🟩'
        case 'found': return '🟨'
        case 'missed': return '⬛️'
        default: return ''
      }
    }

    const shareId = (() => {
      if (!game.date) return '#????'
      const timestamp = parseInt(game.date.replace(/-/g, ''), 10)
      return '#' + (timestamp & 0xFFFFFFFF).toString(32).toUpperCase()
    })()

    const guessCount = (() => {
      if (game.isComplete && game.isWon) return `${game.guesses.length}/${rows}`
      if (game.isComplete && !game.isWon) return `x/${rows}`
      return `*/${rows}`
    })()

    const initStr = `SS ${shareId} ${guessCount}\n`

    // share results to clipboard
    const shareStr = game.guesses.reduce((acc, row) => {
      acc += row.map((char) => getEmoji(char[1])).join('') + '\n'
      return acc
    }, initStr) + ` 🔥 ${ game.streak || '0' }`


    copyToClipboard(shareStr)
  }, [ game, copyToClipboard, showNotification ])

  const renderStreak = () => {
    return `🔥 ${ game?.streak || '0' }`
  }

  const isSelected = (char) => {
    return guess.includes(char)
  }

  const getStatus = (char) => {
    if (!game || !game.guesses || game.guesses.length === 0) return

    const chars = game.guesses.flat()

    // char may have multiple matches, but we only need to check the most valuable one
    if (chars.some(([c, status]) => (char === c && status === 'correct'))) return 'correct'
    if (chars.some(([c, status]) => (char === c && status === 'found'))) return 'found'
    if (chars.some(([c, status]) => (char === c && status === 'missed'))) return 'missed'
  }

  return <>
    <div className="notification" data-type={notification.type} data-visible={notification.visible}>
      { notification.message }
    </div>
    <header className="brand">
      <h1>SanaSeppo</h1>
      <div className="brand__info">{ renderStreak() }</div>
    </header>
    <main className="grid">
      { Array(rows).fill().map((_, i) =>
        <div key={i} className="grid__row">
          { Array(cols).fill().map((_, j) =>
            <div key={j} className="grid__cell">
              { getCell(i, j) }
            </div>
          )}
        </div>
      )}
    </main>
    <div className="keyboard">
      { keyboardLayout.flat().map((char) => {
        if (char === 'JAA') {
          return (
            <button type="button" onClick={handleShare} className="share" data-hilight={game?.isWon} key="share">
              JAA
            </button>
          )
        }

        const charLabel = char === 'Backspace' ? '<' : char === 'Enter' ? 'ENT' : char.toUpperCase()
        const className = char === 'Backspace' ? 'del' : char === 'Enter' ? 'enter' : undefined

        return (
          <button
            type="button"
            value={char}
            onClick={handleCharInput}
            data-selected={isSelected(char)}
            data-state={getStatus(char)}
            className={className}
            key={char}
          >
            {charLabel}
          </button>
        )
      })}
    </div>
  </>
}

export default App
